import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';
import { formatMoney } from '@theme/money-formatting';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets – The fieldset elements.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  /** @type {ResizeNotifier} */
  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  /** @type {HTMLElement | undefined} */
  #addToCartLabel;

  /** @type {string | undefined} */
  #originalAddToCartText;

  /** @type {MutationObserver | undefined} */
  #labelObserver;

  /** @type {string | undefined} */
  #oneTimeProductUrl;

  /** @type {string | undefined} */
  #subscriptionProductUrl;

  /** @type {HTMLElement | undefined} */
  #oneTimeControl;

  /** @type {HTMLElement[]} */
  #subscribeControls = [];

  /** @type {((event: MouseEvent) => void) | undefined} */
  #handleOneTimeClick;

  /** @type {((event: MouseEvent) => void) | undefined} */
  #handleSubscribeClick;

  #getAddToCartLabel() {
    const sectionId = this.dataset.sectionId;
    /** @type {HTMLElement | null} */
    let labelElement = null;

    if (sectionId) {
      labelElement = document.querySelector(
        `#shopify-section-${sectionId} .buy-buttons-block .add-to-cart-text__label`,
      );
    }

    if (!labelElement) {
      const sectionElement = this.closest('section');
      labelElement = sectionElement?.querySelector('.buy-buttons-block .add-to-cart-text__label') ?? null;
    }

    if (!labelElement) {
      labelElement = document.querySelector('.buy-buttons-block .add-to-cart-text__label');
    }

    if (!(labelElement instanceof HTMLElement)) return undefined;

    if (!this.#originalAddToCartText) {
      this.#originalAddToCartText = labelElement.textContent?.trim() || undefined;
    }

    this.#addToCartLabel = labelElement;

    if (!this.#labelObserver) {
      this.#labelObserver = new MutationObserver(() => {
        if (!this.#addToCartLabel) return;
        const expected =
          this.dataset.purchaseMode === 'subscribe'
            ? 'Subscribe now'
            : this.#originalAddToCartText || '';
        const current = this.#addToCartLabel.textContent?.trim() || '';
        if (expected && current !== expected) {
          this.#addToCartLabel.textContent = expected;
        }
      });
      this.#labelObserver.observe(labelElement, {
        childList: true,
        characterData: true,
        subtree: true,
      });
    }

    return labelElement;
  }

  connectedCallback() {
    super.connectedCallback();
    this.#refreshVariantInputsState();

    if (!this.#oneTimeProductUrl) {
      this.#oneTimeProductUrl = this.dataset.oneTimeProductUrl || this.dataset.productUrl;
    }
    if (!this.#subscriptionProductUrl && this.dataset.subscriptionProductUrl) {
      this.#subscriptionProductUrl = this.dataset.subscriptionProductUrl;
    }

    this.addEventListener('change', this.variantChanged.bind(this));
    this.#resizeObserver.observe(this);
    this.updatePerDayPrices();
    const initialMode = this.dataset.purchaseMode === 'one-time' ? 'one-time' : 'subscribe';
    this.#initPurchaseModeControls(initialMode);
    this.#updateSubscriptionText();
  }

  /**
   * Rebuilds internal radio tracking after morph/re-render and optionally syncs product state.
   * @param {boolean} [triggerChangeOnAutoSelect=false]
   */
  #refreshVariantInputsState(triggerChangeOnAutoSelect = false) {
    this.#checkedIndices = [];
    this.#radios = [];
    /** @type {HTMLInputElement[]} */
    const autoSelectedInputs = [];

    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll("input[type='radio']") ?? [])
        .filter((input) => input instanceof HTMLInputElement);
      this.#radios.push(radios);
      this.#checkedIndices.push([]);

      const checkedIndex = radios.findIndex(
        (radio) => radio instanceof HTMLInputElement && (radio.checked || radio.dataset.currentChecked === 'true'),
      );

      if (checkedIndex !== -1) {
        this.#checkedIndices[this.#checkedIndices.length - 1] = [checkedIndex];
        return;
      }

      const availableRadios = radios.filter(
        (radio) =>
          radio instanceof HTMLInputElement &&
          radio.dataset.optionAvailable !== 'false' &&
          radio.disabled === false,
      );

      /*
        Select SECOND value by default on load
        Fallback to first if second doesn't exist
      */
      const autoSelectedRadio = availableRadios[1] || availableRadios[0];

      if (autoSelectedRadio instanceof HTMLInputElement) {
        this.updateSelectedOption(autoSelectedRadio);
        autoSelectedInputs.push(autoSelectedRadio);
      }
    });

    if (triggerChangeOnAutoSelect && autoSelectedInputs.length) {
      const lastAutoSelectedInput = autoSelectedInputs[autoSelectedInputs.length - 1];
      if (lastAutoSelectedInput instanceof HTMLInputElement) {
        lastAutoSelectedInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#labelObserver?.disconnect();
    this.#unbindPurchaseModeControls();
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);

    if (event.target instanceof HTMLInputElement) {
      this.#autoSelectLinkedVariants(event.target);
    }

    this.dispatchEvent(new VariantSelectedEvent({
      id: selectedOption.dataset.optionValueId ?? '',
    }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
        ? 'featured-product-information'
        : undefined;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), morphElementSelector);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * @param {'one-time' | 'subscribe'} initialMode
   */
  #initPurchaseModeControls(initialMode) {
    this.#bindPurchaseModeControls(initialMode);
    const mode = initialMode === 'one-time' ? 'one-time' : 'subscribe';
    this.dataset.purchaseMode = mode;
    this.#updatePurchaseModeUi(mode);
  }

  /**
   * (Re)binds purchase-mode controls without changing the current product.
   * @param {'one-time' | 'subscribe'} initialMode
   */
  #bindPurchaseModeControls(initialMode) {
    this.#unbindPurchaseModeControls();

    const sectionId = this.dataset.sectionId;
    const sectionElement = this.closest('section');

    let oneTimeControl;
    if (sectionId) {
      oneTimeControl = document.querySelector(
        `.purchase-mode__option[data-purchase-mode-control="one-time"][data-section-id="${sectionId}"]`,
      );
    }

    if (!oneTimeControl) {
      oneTimeControl = sectionElement?.querySelector('.purchase-mode__option[data-purchase-mode-control="one-time"]');
    }

    /** @type {HTMLElement[]} */
    let subscribeControls = [];
    if (sectionElement instanceof HTMLElement) {
      subscribeControls = Array.from(
        sectionElement.querySelectorAll('.purchase-mode-footer__option[data-purchase-mode-control="subscribe"]'),
      );
    }
    if (!subscribeControls.length) {
      subscribeControls = Array.from(
        document.querySelectorAll('.purchase-mode-footer__option[data-purchase-mode-control="subscribe"]'),
      );
    }

    if (!oneTimeControl && !subscribeControls.length) {
      return;
    }

    /** @type {(event: MouseEvent) => void} */
    const handleOneTimeClick = (event) => {
      event.preventDefault();
      this.#setPurchaseMode('one-time');
    };
    this.#handleOneTimeClick = handleOneTimeClick;

    /** @type {(event: MouseEvent) => void} */
    const handleSubscribeClick = (event) => {
      event.preventDefault();
      this.#setPurchaseMode('subscribe');
    };
    this.#handleSubscribeClick = handleSubscribeClick;

    if (oneTimeControl instanceof HTMLElement) {
      this.#oneTimeControl = oneTimeControl;
      oneTimeControl.addEventListener('click', handleOneTimeClick);
    }

    subscribeControls.forEach((control) => {
      if (control instanceof HTMLElement) {
        control.addEventListener('click', handleSubscribeClick);
        this.#subscribeControls.push(control);
      }
    });
  }

  #unbindPurchaseModeControls() {
    if (this.#oneTimeControl instanceof HTMLElement && this.#handleOneTimeClick) {
      this.#oneTimeControl.removeEventListener('click', this.#handleOneTimeClick);
    }

    this.#subscribeControls.forEach((control) => {
      if (!(control instanceof HTMLElement) || !this.#handleSubscribeClick) return;
      control.removeEventListener('click', this.#handleSubscribeClick);
    });

    this.#oneTimeControl = undefined;
    this.#subscribeControls = [];
    this.#handleOneTimeClick = undefined;
    this.#handleSubscribeClick = undefined;
  }

  /**
   * Updates the visual state of purchase-mode controls (radios) without triggering any fetches.
   * @param {'one-time' | 'subscribe'} mode
   */
  #updatePurchaseModeUi(mode) {
    const sectionId = this.dataset.sectionId;
    const sectionElement = this.closest('section');

    let oneTimeButton = this.#oneTimeControl;
    if (!(oneTimeButton instanceof HTMLElement) && sectionId) {
      const oneTimeButtonBySection = document.querySelector(
        `.purchase-mode__option[data-purchase-mode-control="one-time"][data-section-id="${sectionId}"]`,
      );
      if (oneTimeButtonBySection instanceof HTMLElement) {
        oneTimeButton = oneTimeButtonBySection;
      }
    }
    if (!(oneTimeButton instanceof HTMLElement)) {
      oneTimeButton = sectionElement?.querySelector('.purchase-mode__option[data-purchase-mode-control="one-time"]') || undefined;
    }
    if (oneTimeButton instanceof HTMLElement) {
      this.#oneTimeControl = oneTimeButton;
    }

    const subscribeBanners = Array.from(
      this.querySelectorAll('.subscription-banner[data-purchase-mode-control="subscribe"]'),
    );
    /** @type {HTMLElement[]} */
    let subscribeModeFooters = [];
    if (sectionElement instanceof HTMLElement) {
      subscribeModeFooters = Array.from(sectionElement.querySelectorAll('[data-purchase-mode-footer]'));
    }
    if (!subscribeModeFooters.length) {
      subscribeModeFooters = Array.from(document.querySelectorAll('[data-purchase-mode-footer]'));
    }
    const subscribeTextNodes = Array.from(this.querySelectorAll('[data-purchase-mode-text-subscribe]'));
    const oneTimeTextNodes = Array.from(this.querySelectorAll('[data-purchase-mode-text-one-time]'));
    const bannerRightNodes = Array.from(this.querySelectorAll('.subscription-banner__right'));
    /** @type {HTMLElement[]} */
    let subscribeFooterButtons = [];
    if (sectionElement instanceof HTMLElement) {
      subscribeFooterButtons = Array.from(
        sectionElement.querySelectorAll('.purchase-mode-footer__option[data-purchase-mode-control="subscribe"]'),
      );
    }
    if (!subscribeFooterButtons.length) {
      subscribeFooterButtons = Array.from(
        document.querySelectorAll('.purchase-mode-footer__option[data-purchase-mode-control="subscribe"]'),
      );
    }

    /** @type {HTMLElement[]} */
    let topOneTimeButtons = [];
    if (sectionElement instanceof HTMLElement) {
      topOneTimeButtons = Array.from(
        sectionElement.querySelectorAll('.purchase-mode__option[data-purchase-mode-control="one-time"]'),
      );
    }
    if (!topOneTimeButtons.length && sectionId) {
      topOneTimeButtons = Array.from(
        document.querySelectorAll(
          `.purchase-mode__option[data-purchase-mode-control="one-time"][data-section-id="${sectionId}"]`,
        ),
      );
    }
    if (!topOneTimeButtons.length) {
      topOneTimeButtons = Array.from(
        document.querySelectorAll('.purchase-mode__option[data-purchase-mode-control="one-time"]'),
      );
    }
    if (!topOneTimeButtons.length && oneTimeButton instanceof HTMLElement) {
      topOneTimeButtons = [oneTimeButton];
    }

    topOneTimeButtons.forEach((button) => {
      if (!(button instanceof HTMLElement)) return;
      button.classList.toggle('purchase-mode__option--active', mode === 'one-time');
      button.hidden = mode === 'one-time';
      const container = button.closest('.purchase-mode');
      if (container instanceof HTMLElement) {
        container.hidden = mode === 'one-time';
      }
    });

    subscribeBanners.forEach((banner) => {
      if (!(banner instanceof HTMLElement)) return;
      banner.classList.add('subscription-banner--active');
    });

    subscribeTextNodes.forEach((node) => {
      if (node instanceof HTMLElement) {
        node.hidden = mode === 'one-time';
      }
    });
    oneTimeTextNodes.forEach((node) => {
      if (node instanceof HTMLElement) {
        node.hidden = mode !== 'one-time';
      }
    });
    bannerRightNodes.forEach((node) => {
      if (node instanceof HTMLElement) {
        node.hidden = mode === 'one-time';
      }
    });

    subscribeModeFooters.forEach((footer) => {
      if (footer instanceof HTMLElement) {
        footer.hidden = mode !== 'one-time';
      }
    });
    subscribeFooterButtons.forEach((button) => {
      if (button instanceof HTMLElement) {
        button.classList.remove('purchase-mode-footer__option--active');
      }
    });
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  updatePerDayPrices() {
    const moneyFormat = this.dataset.moneyFormat;
    const currency = this.dataset.currency;
    
    if (!moneyFormat || !currency) return;

    const daysPerMonth = Number.parseInt(this.dataset.daysPerMonth || '30', 10) || 30;
    if (!daysPerMonth) return;

    const priceNodes = this.querySelectorAll('[data-price-per-day]');

    priceNodes.forEach((priceNode) => {
      if (!(priceNode instanceof HTMLElement)) return;

      const label = priceNode.closest('label');
      if (!(label instanceof HTMLLabelElement)) return;

      const input = label.querySelector('input[data-variant-price][data-months]');
      if (!(input instanceof HTMLInputElement)) return;

      const variantPriceValue = Number.parseInt(input.dataset.variantPrice || '', 10);
      const monthsValue = Number.parseInt(input.dataset.months || '', 10);

      if (!variantPriceValue || !monthsValue) return;

      const totalDays = monthsValue * daysPerMonth;
      if (!totalDays) return;

      const perDayMinorUnits = Math.round(variantPriceValue / totalDays);
      const formatted = formatMoney(perDayMinorUnits, moneyFormat, currency);

      // Only set per-day text when Liquid has not already provided one (metafield)
      if (!priceNode.textContent || priceNode.textContent.trim() === '') {
        priceNode.textContent = `${formatted} / day`;
      }

      const totalPriceNode = label.querySelector('[data-total-price]');
      if (totalPriceNode instanceof HTMLElement) {
        const totalFormatted = formatMoney(variantPriceValue, moneyFormat, currency);
        totalPriceNode.textContent = totalFormatted;
      }
      const comparePriceNode = label.querySelector('[data-compare-price]');

if (comparePriceNode instanceof HTMLElement) {
  const comparePriceValue = Number.parseInt(
    comparePriceNode.dataset.comparePriceValue || '',
    10
  );

  if (comparePriceValue > variantPriceValue) {
    const compareFormatted = formatMoney(
      comparePriceValue,
      moneyFormat,
      currency
    );

    comparePriceNode.textContent = compareFormatted;
    comparePriceNode.hidden = false;
  } else {
    comparePriceNode.hidden = true;
  }
}
    });
  }
  #updateSubscriptionText() {
  const subscribeTextNodes = this.querySelectorAll(
    '[data-purchase-mode-text-subscribe]'
  );

  subscribeTextNodes.forEach((node) => {
    if (!(node instanceof HTMLElement)) return;

    const isIndex3 =
      window.location.pathname.includes('hip-joint-chews-special-offer');

    const index3Text = node.dataset.index3Text;
    const defaultText = node.dataset.defaultText;

    node.textContent = isIndex3
      ? index3Text
      : defaultText;
  });
} 

  /**
   * Loads a different product into this variant picker using existing fetch/morph logic.
   * @param {string} targetProductUrl
   */
  #loadProductByUrl(targetProductUrl) {
    if (!targetProductUrl) return;

    const baseUrl = targetProductUrl.split('?')[0];

    const fakeOption = document.createElement('span');
    fakeOption.dataset.connectedProductUrl = baseUrl;

    const requestUrl = this.buildRequestUrl(fakeOption, 'purchase-mode-switch');

    this.fetchUpdatedSection(requestUrl);
  }

  /**
   * @param {'one-time' | 'subscribe'} mode
   */
  #setPurchaseMode(mode) {
    if (mode !== 'one-time' && mode !== 'subscribe') return;
    this.#applyPurchaseMode(mode, true);
  }

  /**
   * @param {'one-time' | 'subscribe'} mode
   * @param {boolean} resetForOneTime
   */
  #applyPurchaseMode(mode, resetForOneTime) {
    this.dataset.purchaseMode = mode;
    this.#updatePurchaseModeUi(mode);

    const labelElement = this.#getAddToCartLabel();

    if (mode === 'subscribe') {
      if (labelElement) {
        labelElement.textContent = 'Subscribe now';
      }
      const targetUrl =
        this.#subscriptionProductUrl || this.dataset.subscriptionProductUrl || this.dataset.productUrl || '';
      if (targetUrl) {
        this.#loadProductByUrl(targetUrl);
      }
    } else {
      if (this.#originalAddToCartText && labelElement) {
        labelElement.textContent = this.#originalAddToCartText;
      }
      const targetUrl = this.#oneTimeProductUrl || this.dataset.oneTimeProductUrl || this.dataset.productUrl || '';
      if (targetUrl) {
        this.#loadProductByUrl(targetUrl);
      }
      if (resetForOneTime) {
        this.#resetToDefaultVariant();
      }
    }
  }

  #resetToDefaultVariant() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    /** @type {HTMLInputElement[]} */
    const inputsToChange = [];

    fieldsets.forEach((fieldset) => {
      if (!(fieldset instanceof HTMLFieldSetElement)) return;
      const radios = Array.from(
        fieldset.querySelectorAll("input[type='radio']"),
      );
      const firstAvailable =
        radios.find((input) => input instanceof HTMLInputElement && input.dataset.optionAvailable !== 'false') ||
        radios[0];
      if (firstAvailable instanceof HTMLInputElement) {
        inputsToChange.push(firstAvailable);
      }
    });

    if (!inputsToChange.length) return;

    inputsToChange.forEach((input) => {
      this.updateSelectedOption(input);
    });

    const last = inputsToChange[inputsToChange.length - 1];
    if (last instanceof HTMLInputElement) {
      const changeEvent = new Event('change', { bubbles: true });
      last.dispatchEvent(changeEvent);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  /**
 * Auto select first available value in remaining fieldsets
 * after a variant option changes
 * @param {HTMLInputElement} changedInput
 */
#autoSelectLinkedVariants(changedInput) {
  const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

  const changedFieldsetIndex = Number.parseInt(
    changedInput.dataset.fieldsetIndex || '',
    10
  );

  fieldsets.forEach((fieldset, index) => {

    // Skip current changed fieldset
    if (index <= changedFieldsetIndex) return;

    const radios = Array.from(
      fieldset.querySelectorAll("input[type='radio']")
    ).filter(
      (radio) =>
        radio instanceof HTMLInputElement &&
        radio.dataset.optionAvailable !== 'false' &&
        !radio.disabled
    );

    if (!radios.length) return;

    /*
      Select SECOND value automatically
      If second doesn't exist then fallback to first
    */
    const autoSelectRadio = radios[1] || radios[0];

    if (
      autoSelectRadio instanceof HTMLInputElement &&
      !autoSelectRadio.checked
    ) {
      this.updateSelectedOption(autoSelectRadio);
    }
  });
}
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, morphElementSelector) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        let newProduct;

        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          newProduct = this.updateVariantPicker(html);
        }

        // Dispatch for all paths so product-form-component can reset #variantChangeInProgress
        if (this.selectedOptionId) {
          this.dispatchEvent(
            new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
              html,
              productId: this.dataset.productId ?? '',
              newProduct,
            })
          );
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const previousMode = this.dataset.purchaseMode === 'one-time' ? 'one-time' : 'subscribe';

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });
    this.#refreshVariantInputsState(true);
    this.updateVariantPickerCss();
    this.updatePerDayPrices();
    this.#bindPurchaseModeControls(previousMode);
    this.dataset.purchaseMode = previousMode;
    this.#updatePurchaseModeUi(previousMode);
this.#updateSubscriptionText();
    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}